/**
 * NpcArchetypesServiceModule — 2026-06-08 Phase 6 wire-up for the canonical
 * Core Rulebook §28 NPC archetype generator tables (seed:
 * seed/canonical/npc_creation/npc_archetypes.json, route: Phase 6 commit).
 *
 * Routes (per briefing_room_backend/routes/npc_archetypes.py):
 *   GET /api/modules/briefing-room/npc-archetypes             → ArchetypeManifest
 *   GET /api/modules/briefing-room/npc-archetypes/{id}        → full entry | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders().
 */
import { getAuthHeaders } from './authHeaders';

export type ArchetypeCategory = 'allies' | 'enemies' | 'neutrals';

export interface ArchetypeManifestEntry {
  id: string;
  name: string;
  category: ArchetypeCategory;
  manifest_section: string;
  description_page: number | null;
  table_page: number | null;
}

export interface ArchetypeManifest {
  archetypes: ArchetypeManifestEntry[];
  total: number;
  categories_complete: string[];
  schema_version: string;
  source: string;
}

const BASE_PATH = '/api/modules/briefing-room/npc-archetypes';

export class NpcArchetypesServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async list(): Promise<ArchetypeManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`NpcArchetypesServiceModule: list HTTP ${resp.status}`);
    }
    return (await resp.json()) as ArchetypeManifest;
  }

  async get(archetypeId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(archetypeId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(`NpcArchetypesServiceModule: get HTTP ${resp.status}`);
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
